"use client";

import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Copy, Loader2 } from "lucide-react";
import QRCode from "react-qr-code";
import { useScopedTranslations } from "@/lib/i18n/useScopedTranslations";
import { Lock } from "lucide-react";

function getTxExplorerUrl(
  chain: string,
  txHash: string
): string {
  const c = chain.toLowerCase();

  if (c === "bsc") {
    return `https://bscscan.com/tx/${txHash}`;
  }

  if (c === "bsc-test") {
    return `https://testnet.bscscan.com/tx/${txHash}`;
  }

  if (c === "base") {
    return `https://basescan.org/tx/${txHash}`;
  }

  if (c === "base-test") {
    return `https://sepolia.basescan.org/tx/${txHash}`;
  }

  // fallback de seguridad
  return "#";
}


type Step =
  | "init"
  | "ready"
  | "countdown"
  | "pending"
  | "success"
  | "failed";

export function CryptoCheckoutPanel({
  amount,
  token,          // etiqueta UI: USDC / USDT
  network,
  expiresIn = 60,

  // 🔑 contexto
  productId,
  sellerId,
  userId,
  customerEmail,
  aiAgentId,

  platformFee = 0,
  markup = 0,
  markupWallet = null,
}: {
  amount: string;
  token: string;
  network: string;
  expiresIn?: number;

  productId: number;
  sellerId: number;
  userId?: number;
  customerEmail?: string;
  aiAgentId?: number | null

  platformFee?: number;
  markup?: number;
  markupWallet?: string | null;
}) {
  const [step, setStep] = useState<Step>("init");
  const [copied, setCopied] = useState(false);

  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [received, setReceived] = useState<number>(0);
  const [polling, setPolling] = useState(false);
  const [intentId, setIntentId] = useState<number | null>(null);
  const [mode, setMode] = useState<"test" | "live">("live");
  const [hasStartedPayment, setHasStartedPayment] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<number | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [chain, setChain] = useState<string | null>(null);
  
  const NETWORK_LABELS: Record<string, string> = {
    bsc: "BNB Smart Chain (BEP-20)",
    eth: "Ethereum (ERC-20)",
    base: "Base (ERC-20)",
    polygon: "Polygon (ERC-20)",
    tron: "TRON (TRC-20)",

    // aliases defensivos
    bep20: "BNB Smart Chain (BEP-20)",
    erc20: "Ethereum (ERC-20)",
    trc20: "TRON (TRC-20)",
  };

  const networkLabel = useMemo(() => {
    if (!network) return "";

    const key = network.toLowerCase();
    return NETWORK_LABELS[key] || network.toUpperCase();
  }, [network]);


  const { t, isLoading: i18nLoading } = useScopedTranslations('payment/crypto')

  const generateIntent = async () => {
    try {
      setLoadingWallet(true);

      // Crear payment intent
      const intentRes = await fetch("/api/crypto/create-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chain: network.toLowerCase(),

          token,
          currency: token,           // 👈 etiqueta (USDC / USDT)

          expectedAmount: amount,

          productId,
          sellerId,
          userId,
          customerEmail,
          aiAgentId,

          platformFee,
          markup,
          markupWallet,
        }),
      });

      const intentData = await intentRes.json();
      if (!intentRes.ok || !intentData.intentId) {
        throw new Error("Failed to create payment intent");
      }

      setIntentId(intentData.intentId);
      setWalletAddress(intentData.walletAddress);
      setChain(intentData.chain);
      setMode(intentData.mode);
      setReceived(0);
      setStep("ready");
      setPolling(true);

    } catch (err) {
      console.error("❌ Wallet generation error:", err);
      setStep("failed");
    } finally {
      setLoadingWallet(false);
    }
  };

  const copyAddress = async () => {
    if (!walletAddress) return;
    await navigator.clipboard.writeText(walletAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const checkBalance = async () => {
    if (!intentId) return;

    try {
      const res = await fetch("/api/crypto/check-balance", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intentId }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) return;

      const current = Number(data.received || 0);
      const target = Number(amount);

      setReceived(current);

      if (current >= target) {
          setPolling(false);
          setStep("pending");               // 👈 NO success

          proceedPayment();                 // 👈 ahora sí
          return;
      }

      if (current > 0) {
        setStep("pending");
      } else {
        setStep(hasStartedPayment ? "countdown" : "ready");
      }

      if (data.status === "expired" || data.status === "failed") {
        setStep("failed");
        setPolling(false);
      }
    } catch (err) {
      console.error("❌ Balance polling error:", err);
    }
  };

  const proceedPayment = async () => {
    if (!intentId) return;
    if (isProcessingPayment) return; // ok, pero ya no lo seteas antes de llamarla
    setIsProcessingPayment(true);

    try {
      const res = await fetch("/api/crypto/proceed-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intentId }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
          console.error("Proceed-payment failed:", data);
          throw new Error(data?.message || "Relay failed");
        }

        // 🟢 SI ESTÁ PROCESANDO, NO ES ERROR
        if (data.processing) {
          setTimeout(() => proceedPayment(), 1500);
          return; // deja que el polling siga
        }

        // 🟢 SI YA ESTABA PROCESADO
        if (data.alreadyProcessed && data.txHash) {
          return data;
        }

        // 🟢 SI ES ÉXITO NUEVO
        if (data.txHash) {
          setTxHash(data.txHash);
          setOrderId(data.orderId ?? null);
          setChain(data.chain ?? chain ?? null);
          setStep("success");
          setPolling(false); // ya no necesitamos más polling
          return;
        }
        // (si llegó aquí, algo está raro: success=true pero sin txHash)
        console.error("Proceed-payment: success without txHash:", data);
        throw new Error("Relay success but missing txHash");
    } catch (err) {
      console.error("❌ proceed-payment error:", err);
      setStep("failed");
    } finally {
      setIsProcessingPayment(false);
    }
  };


  useEffect(() => {
    if (!polling || !intentId) return;
    if (step === "success" || step === "failed") return;

    // chequeo inmediato
    checkBalance();

    // luego cada 4s
    const interval = setInterval(checkBalance, 4000);

    return () => clearInterval(interval);
  }, [polling, intentId, step, hasStartedPayment]);

  return (
    <div
      className="
        w-full max-w-md mx-auto rounded-2xl p-6 space-y-6
        bg-white border border-border shadow-lg
        dark:bg-[#0B0F1A] dark:border-white/10 dark:shadow-xl
      "
    >
      {/* HEADER */}
      <div className="text-center space-y-2">
        <h2 className="text-lg font-semibold text-foreground">
          {!i18nLoading && t('heading_pay_crypto')}
        </h2>

        <p className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
          <Lock className="w-4 h-4 text-green-600" />
          {!i18nLoading && t('text_secure_payment')}
        </p>


        {/* MODE BADGE — only after intent exists */}
        {intentId && mode === "test" && (
          <div className="flex justify-center">
            <span
              className="
                px-3 py-1 rounded-full text-xs font-medium
                bg-yellow-500/10 text-yellow-600 border border-yellow-500/30
              "
            >
              TEST MODE
            </span>
          </div>
        )}
      </div>


      {/* STEP: INIT */}
      {step === "init" && (
        <div className="text-center space-y-4">
          <p className="text-sm text-muted-foreground">
            {!i18nLoading && t('text_create_temp_wallet')}
          </p>

          <div className="rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-xs p-3">
            {!i18nLoading && (
              <span
                dangerouslySetInnerHTML={{
                  __html: t('text_disclaimer_temp_wallet'),
                }}
              />
            )}
          </div>

          <Button
            className="w-full h-11 rounded-xl text-base"
            onClick={generateIntent}
            disabled={loadingWallet}
          >
            {loadingWallet ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {!i18nLoading && t('text_creating_temp_wallet')}
              </>
            ) : (
              !i18nLoading && t('button_create_wallet')
            )}
          </Button>

        </div>
      )}

      {/* STEP: READY */}
      {step === "ready" && (
        <div className="space-y-5 text-center">

          <p className="text-sm text-muted-foreground">
            {!i18nLoading && t('text_please_send')}
          </p>

          <div className="text-3xl font-bold text-foreground">
            {amount} {token}
          </div>

          <div className="text-md text-muted-foreground">
            {!i18nLoading && t('text_network')} <b className="text-foreground">{networkLabel}</b> {!i18nLoading && t('text_address')}
          </div>

          {/* QR */}
          <div className="flex justify-center bg-white p-3 rounded-xl border border-border">
            {walletAddress && <QRCode value={walletAddress} size={160} />}
          </div>

          {/* ADDRESS */}
          <div
            className="
              rounded-xl p-3 flex items-center justify-between gap-2
              bg-muted/40 border border-border
              dark:bg-white/5 dark:border-white/10
            "
          >
            <span className="text-xs font-mono text-foreground truncate">
              {walletAddress || "—"}
            </span>
            <button onClick={copyAddress} className="text-muted-foreground hover:text-foreground">
              <Copy size={16} />
            </button>
          </div>

          {copied && (
            <p className="text-xs text-green-400">{!i18nLoading && t('text_address_copied')}</p>
          )}

          <Button
            variant="secondary"
            className="w-full rounded-xl"
            onClick={() => {
              setHasStartedPayment(true);
              setStep("countdown"); // solo visual
            }}
          >
            {!i18nLoading && t('button_payment_sent')}
          </Button>



        </div>
      )}

      {/* STEP: COUNTDOWN */}
      {step === "countdown" && (
        <div className="flex flex-col items-center space-y-4 py-6">
          <div className="relative w-24 h-24">
            <div className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-foreground">
              {expiresIn}
            </div>
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="rgba(255,255,255,0.1)"
                strokeWidth="8"
                fill="none"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke="#22c55e"
                strokeWidth="8"
                fill="none"
                strokeDasharray="282"
                strokeDashoffset="40"
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
              />
            </svg>
          </div>

          <p className="text-sm text-muted-foreground">
            {!i18nLoading && t('text_waiting_confirmation')}
          </p>
        </div>
      )}

      {/* STEP: PENDING */}
      {step === "pending" && (
        <div className="flex flex-col items-center space-y-3 py-6 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-foreground" />
          <p className="text-sm text-muted-foreground">
            {!i18nLoading && t('text_received')} <b>{received}</b> {!i18nLoading && t('text_of')} <b>{amount}</b> {token}
            <br />
            {!i18nLoading && t('text_waiting_confirmation')}
          </p>

          <div className="w-full max-w-xs mt-2">
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all duration-500"
                style={{
                  width: `${Math.min(
                    (received / Number(amount)) * 100,
                    100
                  )}%`,
                }}
              />
            </div>

            <p className="text-xs text-muted-foreground mt-1">
              {received} / {amount} {token}
            </p>
          </div>

        </div>
      )}

      {/* STEP: SUCCESS */}
      {step === "success" && (
        <div className="flex flex-col items-center space-y-4 py-8">
          <CheckCircle2 className="w-14 h-14 text-green-500" />
          <h3 className="text-lg font-semibold text-foreground">
            {!i18nLoading && t('heading_payment_success')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {!i18nLoading && t('text_transaction_confirmed')}
          </p>
        </div>
      )}

      {step === "success" && txHash && chain && (
        <div className="mt-6 flex flex-col items-center space-y-3">
          <Button asChild className="w-full max-w-xs">
            <a
              href="/settings/orders"
            >
              {!i18nLoading && t('button_order')}
            </a>
          </Button>

          <Button variant="outline" asChild className="w-full max-w-xs">
            <a
              href={getTxExplorerUrl(chain, txHash)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {!i18nLoading && t('button_transaction')} {chain.toUpperCase()}
            </a>
          </Button>
        </div>
      )}


      {/* STEP: FAILED */}
      {step === "failed" && (
        <div className="text-center text-red-400 text-sm">
          {!i18nLoading && t('text_payment_failed')}
        </div>
      )}
    </div>
  );
}
